import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pick from 'lodash/pick';
import { moment } from '@eb/date';

import { datePropType, getAdditionalProps } from '@eb/eds-utils';
import {
    DEFAULT_INPUT_DISPLAY_FORMAT,
    ISO_8601_DATE_FORMAT,
} from '@eb/eds-utils';

import SingleDatePickerController from './_internal/SingleDatePickerController';

import {
    COLOR_PROP,
    BLUE_COLOR,
    DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES,
} from './constants';

import './datePicker.scss';

const getMinDate = (startDate, isEndInput) =>
    //if end input, don't let user select date before the start date
    startDate && isEndInput ? startDate : null;

const getMaxDate = (endDate, isEndInput) =>
    //if not the endInput, don't let the user select a date after the end date
    endDate && !isEndInput
        ? moment(endDate).add(1, 'days').format(ISO_8601_DATE_FORMAT)
        : null;

/*
In some use cases we may want a range, but with the inputs for start and
end separated. To account for this, additional state will need to be
maintained by the consumer of the component but should allow for the flexibility
the user is looking for.
*/
export default class DateRangePickerSplitInput extends PureComponent {
    static propTypes = {
        /**
         * For a reference to all available properties we forward to
         * `react-dates` check https://github.com/airbnb/react-dates#daterangepicker
         */
        ...DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES,
        /**
         * Initial date. Value displayed in the input
         */
        defaultValue: datePropType,
        /**
         * Current selected date. Value displayed in the input
         */
        value: datePropType,
        /**
         * ID of element
         */
        id: PropTypes.string,
        /**
         * Name of element
         */
        name: PropTypes.string,
        /**
         * Minimum date that can be chosen
         */
        minDate: datePropType,
        /**
         * Maximum date that can be chosen
         */
        maxDate: datePropType,
        /**
         * First day of date range
         */
        startDate: datePropType,
        /**
         * Last day of date range
         */
        endDate: datePropType,
        /**
         * Set error state
         */
        hasError: PropTypes.bool,
        /**
         * {function} for handling date selection
         */
        onChange: PropTypes.func,
        /**
         * {function} for handling blur event
         */
        onBlur: PropTypes.func,
        /**
         * {function} onFocus
         */
        onFocus: PropTypes.func,
        /**
         * Moment formatting date string to dictate display format of selected
         * dates
         */
        inputDisplayFormat: PropTypes.string,
        /**
         * Calendar color scheme
         */
        color: PropTypes.oneOf(COLOR_PROP),
        /**
         * Indicator for the end input which informs which value to update
         */
        isEndInput: PropTypes.bool,
        /**
         * {function} Determines if a given day should be highlighted on the calendar.
         * The function receives a `moment()` wrapped date as paremeter and should return
         * a Boolean value indicating wheter to highlight the day or not.
         * Example:
         * Highlight Fridays:
         * ```js
         * <DatePicker isDayHighlighted={(day) => day.format('ddd') == 'Fri'}/>
         * ```
         */
        isDayHighlighted: PropTypes.func,
        /**
         * {function} Determines if a given day should be blocked on the calendar.
         * The function receives a `moment()` wrapped date as parameter and should return
         * a Boolean value indicating whether to block the day or not.
         * Blocked days are disabled and cannot be selected.
         * Example:
         * Block Weekends:
         * ```js
         * <DatePicker isDayBlocked={(day) => Boolean(day.format('ddd').match(/Sat|Sun/))}/>
         * ```
         */
        isDayBlocked: PropTypes.func,
    };

    static defaultProps = {
        color: BLUE_COLOR,
        inputDisplayFormat: DEFAULT_INPUT_DISPLAY_FORMAT,
    };

    render() {
        const {
            color,
            startDate,
            endDate,
            inputDisplayFormat,
            id,
            name,
            minDate,
            maxDate,
            hasError,
            onChange,
            onBlur,
            onFocus,
            value,
            defaultValue,
            isEndInput,
            isDayHighlighted,
            isDayBlocked,
        } = this.props;

        const classes = classNames(
            'eds-date-picker',
            'eds-date-picker--split',
            `eds-date-picker--${color}`,
        );

        const extraAttrs = getAdditionalProps(this);
        const dateRangePickerExtraAttrs = pick(
            this.props,
            Object.keys(DAY_PICKER_RANGE_CONTROLLER_PROP_TYPES),
        );

        if (
            moment(startDate).isAfter(moment(endDate)) &&
            process.env.NODE_ENV !== 'production'
        ) {
            throw new Error(
                `Invalid dates provided. Start date (${startDate}) cannot come after end date (${endDate})`,
            );
        }

        return (
            <SingleDatePickerController
                {...extraAttrs}
                {...dateRangePickerExtraAttrs}
                startDate={startDate}
                endDate={endDate}
                inputDisplayFormat={inputDisplayFormat}
                value={value}
                defaultValue={defaultValue}
                containerClasses={classes}
                id={id}
                name={name}
                minDate={minDate || getMinDate(startDate, isEndInput)}
                maxDate={maxDate || getMaxDate(endDate, isEndInput)}
                hasError={hasError}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                isEndInput={isEndInput}
                isDayHighlighted={isDayHighlighted}
                isDayBlocked={isDayBlocked}
            />
        );
    }
}
